import { useEffect, RefObject } from 'react';
const dataGridSelector = '[role="table"]';

export interface UseDataGridInfiniteScrollOptions {
  dataGridRef: RefObject<HTMLElement>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Distance from bottom (in pixels) to trigger load more. Default: 100 */
  threshold?: number;
}

export const useDataGridInfiniteScroll = ({
  dataGridRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  threshold = 100,
}: UseDataGridInfiniteScrollOptions) => {
  useEffect(() => {
    let scrollableElement: HTMLElement | null = null;

    const findScrollableContainer = (): HTMLElement | null => {
      if (!dataGridRef.current) return null;

      const tableElement = dataGridRef.current.querySelector(dataGridSelector);
      if (!tableElement) return null;

      const scrollContainer = tableElement.parentElement;
      if (!scrollContainer) return null;

      const style = window.getComputedStyle(scrollContainer);
      if (
        scrollContainer.scrollHeight > scrollContainer.clientHeight &&
        (style.overflowY === 'auto' || style.overflowY === 'scroll')
      ) {
        return scrollContainer;
      }

      return null;
    };

    const handleScroll = () => {
      if (!scrollableElement || !hasNextPage || isFetchingNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

      if (scrollHeight - scrollTop - clientHeight < threshold) {
        fetchNextPage();
      }
    };

    const setupScrollListener = () => {
      scrollableElement = findScrollableContainer();

      if (scrollableElement) {
        scrollableElement.addEventListener('scroll', handleScroll, {
          passive: true,
        });
      } else {
        // Retry after a short delay if the scrollable element isn't fully rendered yet
        setTimeout(setupScrollListener, 100);
      }
    };

    setupScrollListener();

    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [dataGridRef, hasNextPage, isFetchingNextPage, fetchNextPage, threshold]);
};
